import React, { useState, useMemo } from "react";
import { Row, Col, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Table } from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import {
  useGetAllTargetsQuery,
  useCreateTargetMutation,
  useUpdateTargetMutation
} from "store/features/target/targetApi";
import { useGetAllProjectsQuery } from "store/features/client/clientManagementApi";
import { useGetAllDevelopersQuery, useGetAllProjectManagersQuery, useGetAllDesignersQuery } from "store/features/developer/developerManagementApi";
import { useGetAllUsersQuery } from "store/features/users/userManagementApi";
import { toast } from "sonner";

const TargetManagement = () => {
  const user = useSelector(selectUser);
  const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_");
  const isAdmin = cleanRole === "super_admin" || cleanRole === "admin";
  const currentMonthStr = new Date().toISOString().slice(0, 7); // e.g. "2026-08"
  
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserForTarget, setSelectedUserForTarget] = useState("");
  const [targetAmountInput, setTargetAmountInput] = useState("");
  const [commissionRateInput, setCommissionRateInput] = useState("5");
  const [notesInput, setNotesInput] = useState("");
  const [editingTargetId, setEditingTargetId] = useState(null);

  const { data: targets = [], isLoading: targetsLoading, refetch: refetchTargets } = useGetAllTargetsQuery();
  const { data: projects = [], isLoading: projectsLoading } = useGetAllProjectsQuery();
  const { data: developers = [] } = useGetAllDevelopersQuery();
  const { data: projectManagers = [] } = useGetAllProjectManagersQuery();
  const { data: designers = [] } = useGetAllDesignersQuery();
  const { data: allUsers = [] } = useGetAllUsersQuery();

  const [createTarget, { isLoading: isCreating }] = useCreateTargetMutation();
  const [updateTarget, { isLoading: isUpdating }] = useUpdateTargetMutation();

  // Combine all team members/users
  const allTeamUsers = useMemo(() => {
    const map = new Map();
    if (Array.isArray(allUsers)) {
      allUsers.forEach(u => {
        if (u && u.id) {
          map.set(String(u.id), {
            id: String(u.id),
            name: u.nickname || u.first_name || u.username || `User #${u.id}`,
            role: u.role || 'Marketing / Team'
          });
        }
      });
    }
    const addUsers = (list, roleDefault) => {
      if (!Array.isArray(list)) return;
      list.forEach(u => {
        if (u && u.id && !map.has(String(u.id))) {
          map.set(String(u.id), {
            id: String(u.id),
            name: u.nickname || u.name || u.user_info?.name || `User #${u.id}`,
            role: u.role || roleDefault
          });
        }
      });
    };
    addUsers(developers, 'Developer');
    addUsers(projectManagers, 'Project Manager');
    addUsers(designers, 'Designer');
    return Array.from(map.values());
  }, [allUsers, developers, projectManagers, designers]);

  // Function to calculate user's achieved amount from assigned projects for selected month
  const calculateUserAchievedAmount = (userId, targetMonth = selectedMonth, userRole = "") => {
    if (!projects || !Array.isArray(projects)) return { totalAchieved: 0, projectBreakdown: [] };

    let totalAchieved = 0;
    const projectBreakdown = [];

    projects.forEach(project => {
      // Gather assigned members for this project
      const memberIds = new Set();
      const addList = (list) => {
        if (!list) return;
        const items = Array.isArray(list) ? list : [list];
        items.forEach(item => {
          const id = typeof item === 'object' && item !== null ? item.id : item;
          if (id) memberIds.add(String(id));
        });
      };

      addList(project.assigned_project_manager);
      addList(project.assigned_frontend_dev);
      addList(project.assigned_backend_dev);
      addList(project.assigned_designer);
      addList(project.assigned_team);
      addList(project.assigned_marketing_officer);
      if (project.marketing_officer_id) memberIds.add(String(project.marketing_officer_id));

        // Helper to check if a member ID belongs to a marketing officer
        const isMarketingId = (mId) => {
          const moField = project.assigned_marketing_officer;
          if (moField) {
            const items = Array.isArray(moField) ? moField : [moField];
            for (const item of items) {
              const itemId = typeof item === 'object' && item !== null ? item.id : item;
              if (String(itemId) === String(mId)) return true;
            }
          }
          if (project.marketing_officer_id && String(project.marketing_officer_id) === String(mId)) {
            return true;
          }
          const foundUser = allTeamUsers.find(u => String(u.id) === String(mId));
          if (foundUser && foundUser.role && (foundUser.role.toLowerCase().includes('marketing') || foundUser.role.toLowerCase().includes('sales'))) {
            return true;
          }
          return false;
        };

      if (memberIds.has(String(userId))) {
        const pDate = project.start_date || project.created_at || project.systemdate;
        const projectStartMonth = (pDate && typeof pDate === "string") ? pDate.slice(0, 7) : targetMonth;

        // Include project if it started in or before targetMonth
        if (projectStartMonth <= targetMonth) {
          const isMarketingOfficer = isMarketingId(userId);

          // Count only technical team members (excluding marketing officers)
          const techMemberIds = Array.from(memberIds).filter(mId => !isMarketingId(mId));

          const N = (isMarketingOfficer ? 1 : techMemberIds.length) || 1;
          const projectBudget = parseFloat(project.budget) || 0;
          const memberBudgetShare = projectBudget / N;

          let memberAchieved = 0;
          let displayProgressPercent = 0;

          if (isMarketingOfficer) {
            // For Marketing Officers, 100% of project budget is added to Achieved Target upon assignment immediately
            memberAchieved = projectBudget;
            displayProgressPercent = 100;
          } else {
            // For technical team (developers/PMs/designers), calculate progress-based achievement directly from developer_progress
            let devProgressData = project.developer_progress || {};
            if (typeof devProgressData === 'string') {
              try {
                devProgressData = JSON.parse(devProgressData);
              } catch (e) {
                devProgressData = {};
              }
            }

            const currentDevProgress = Number(devProgressData[userId] ?? devProgressData[String(userId)] ?? 0);
            displayProgressPercent = Math.round(currentDevProgress);
            memberAchieved = (memberBudgetShare * displayProgressPercent) / 100;
          }

          totalAchieved += memberAchieved;

          projectBreakdown.push({
            projectId: project.id,
            projectName: project.name,
            totalBudget: projectBudget,
            assignedCount: N,
            shareBudget: isMarketingOfficer ? projectBudget : memberBudgetShare,
            progressPercent: displayProgressPercent,
            achievedAmount: memberAchieved,
            isMarketing: isMarketingOfficer
          });
        }
      }
    });

    return { totalAchieved, projectBreakdown };
  };

  // Build Target Table Rows
  const userTargetRows = useMemo(() => {
    const rows = [];
    const filteredTargets = targets.filter(t => t.month === selectedMonth);

    // If user is developer/designer/PM, show only their target unless super_admin/admin
    const usersToDisplay = isAdmin
      ? allTeamUsers
      : allTeamUsers.filter(u => String(u.id) === String(user?.id));

    usersToDisplay.forEach(u => {
      const targetObj = filteredTargets.find(t => String(t.user) === String(u.id) || String(t.user?.id) === String(u.id));
      const targetAmount = targetObj ? parseFloat(targetObj.target_amount) || 0 : 0;
      const { totalAchieved, projectBreakdown } = calculateUserAchievedAmount(u.id, selectedMonth, u.role);
      const remainingTarget = Math.max(0, targetAmount - totalAchieved);
      const achievementPercent = targetAmount > 0 ? Math.min(100, (totalAchieved / targetAmount) * 100) : 0;

      rows.push({
        user: u,
        targetObj,
        targetAmount,
        totalAchieved,
        remainingTarget,
        achievementPercent,
        projectBreakdown
      });
    });

    return rows;
  }, [targets, selectedMonth, allTeamUsers, projects, user]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setEditingTargetId(null);
      setSelectedUserForTarget("");
      setTargetAmountInput("");
      setCommissionRateInput("15");
      setNotesInput("");
    }
  };

  const handleOpenSetTargetModal = (row) => {
    if (row.targetObj) {
      setEditingTargetId(row.targetObj.id);
      setSelectedUserForTarget(row.user.id);
      setTargetAmountInput(row.targetAmount.toString());
      setCommissionRateInput(row.targetObj.commission_rate ? row.targetObj.commission_rate.toString() : "15");
      setNotesInput(row.targetObj.notes || "");
    } else {
      setEditingTargetId(null);
      setSelectedUserForTarget(row.user.id);
      setTargetAmountInput("");
      setCommissionRateInput("15");
      setNotesInput("");
    }
    setModalOpen(true);
  };

  const handleSaveTarget = async (e) => {
    e.preventDefault();
    if (!selectedUserForTarget || !targetAmountInput) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const payload = {
        user: parseInt(selectedUserForTarget),
        month: selectedMonth,
        target_amount: parseFloat(targetAmountInput) || 0,
        commission_rate: parseFloat(commissionRateInput) || 15.0,
        notes: notesInput
      };

      if (editingTargetId) {
        await updateTarget({ id: editingTargetId, ...payload }).unwrap();
      } else {
        await createTarget(payload).unwrap();
      }
      refetchTargets();
      toggleModal();
    } catch (err) {
      console.error("Save target error:", err);
      toast.error("Failed to save target");
    }
  };

  if (targetsLoading || projectsLoading) {
    return <DashboardLoader />;
  }

  // Summary Metrics
  const totalTargetSum = userTargetRows.reduce((sum, r) => sum + r.targetAmount, 0);
  const totalAchievedSum = userTargetRows.reduce((sum, r) => sum + r.totalAchieved, 0);
  const overallPercent = totalTargetSum > 0 ? (totalAchievedSum / totalTargetSum) * 100 : 0;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Monthly Targets & Achievements" breadcrumbItem="Targets" />

        {/* Top Controls & Month Filter */}
        <Row className="align-items-center mb-4">
          <Col lg={6}>
            <h4 className="card-title text-primary m-0" style={{ fontSize: "20px" }}>
              <i className="fas fa-bullseye me-2"></i>Monthly Performance Targets
            </h4>
            <p className="text-muted small mb-0">Track monthly earnings targets vs. progress-based achievement.</p>
          </Col>
          <Col lg={6} className="d-flex justify-content-end align-items-center gap-3">
            <div>
              <Label className="me-2 fw-semibold mb-0">Select Month:</Label>
              <Input
                type="month"
                className="d-inline-block"
                style={{ width: "180px" }}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </div>
            {isAdmin && (
              <Button color="primary" onClick={() => { setEditingTargetId(null); setModalOpen(true); }}>
                <i className="fas fa-plus me-1"></i> Set New Target
              </Button>
            )}
          </Col>
        </Row>

        {/* Summary Metrics Cards */}
        <Row className="mb-4">
          <Col lg={4}>
            <Card className="bg-primary text-white">
              <CardBody>
                <h6 className="text-white-50">{isAdmin ? `Total Target Amount (${selectedMonth})` : `My Target Amount (${selectedMonth})`}</h6>
                <h3 className="text-white mb-0">BDT {totalTargetSum.toLocaleString()}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="bg-success text-white">
              <CardBody>
                <h6 className="text-white-50">{isAdmin ? `Total Achieved BDT (${selectedMonth})` : `My Achieved BDT (${selectedMonth})`}</h6>
                <h3 className="text-white mb-0">BDT {totalAchievedSum.toLocaleString(undefined, { maximumFractionDigits: 0 })}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="bg-info text-white">
              <CardBody>
                <h6 className="text-white-50">{isAdmin ? "Overall Target Completion" : "My Target Completion"}</h6>
                <h3 className="text-white mb-0">{overallPercent.toFixed(1)}%</h3>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Main Target List */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h5 className="card-title text-dark mb-4">{isAdmin ? "Team Target & Achievement Table" : "My Target & Achievement"}</h5>
                <div className="table-responsive">
                  <Table className="table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Team Member</th>
                        <th>Role</th>
                        <th>Target Amount (BDT)</th>
                        <th>Achieved Amount (BDT)</th>
                        <th>Remaining (BDT)</th>
                        <th>Achievement Progress</th>
                        {isAdmin && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {userTargetRows.length > 0 ? (
                        userTargetRows.map(row => (
                          <React.Fragment key={row.user.id}>
                            <tr>
                              <td className="fw-bold text-dark">{row.user.name}</td>
                              <td><Badge color="light" className="text-dark">{row.user.role}</Badge></td>
                              <td className="fw-semibold text-primary">
                                ৳ {row.targetAmount.toLocaleString()}
                              </td>
                              <td className="fw-semibold text-success">
                                ৳ {row.totalAchieved.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="fw-semibold text-danger">
                                ৳ {row.remainingTarget.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ minWidth: "200px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="progress flex-grow-1" style={{ height: "10px" }}>
                                    <div
                                      className={`progress-bar ${row.achievementPercent >= 100 ? 'bg-success' : row.achievementPercent >= 50 ? 'bg-info' : 'bg-warning'}`}
                                      role="progressbar"
                                      style={{ width: `${row.achievementPercent}%` }}
                                      aria-valuenow={row.achievementPercent}
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    ></div>
                                  </div>
                                  <span className="fw-bold fs-12">{row.achievementPercent.toFixed(1)}%</span>
                                </div>
                              </td>
                              {isAdmin && (
                                <td>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleOpenSetTargetModal(row)}
                                  >
                                    <i className="fas fa-edit me-1"></i>
                                    {row.targetObj ? "Edit Target" : "Set Target"}
                                  </Button>
                                </td>
                              )}
                            </tr>
                            {/* Project Breakdown Sub-Row */}
                            {row.projectBreakdown.length > 0 && (
                              <tr>
                                <td colSpan={isAdmin ? 7 : 6} className="bg-light p-3">
                                  <div className="ms-3">
                                    <h6 className="fs-12 text-muted fw-bold mb-2">Project-wise Achievement Breakdown for {row.user.name}:</h6>
                                    <Row className="g-2">
                                      {row.projectBreakdown.map(p => (
                                        <Col lg={4} md={6} key={p.projectId}>
                                          <div className="p-2 border rounded bg-white">
                                            <div className="fw-bold fs-13 text-primary mb-1">{p.projectName}</div>
                                            <div className="d-flex justify-content-between fs-11 text-muted">
                                              <span>Total Budget: ৳{p.totalBudget.toLocaleString()}</span>
                                              <span>{p.isMarketing ? "(Marketing Officer)" : `(${p.assignedCount} Devs assigned)`}</span>
                                            </div>
                                            <div className="d-flex justify-content-between fs-11 text-muted mb-1">
                                              <span>{p.isMarketing ? "Value Share:" : "Dev Share:"} ৳{p.shareBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                              <span>Progress: {p.isMarketing ? "100% (Assigned)" : `${p.progressPercent}%`}</span>
                                            </div>
                                            <div className="fw-semibold text-success fs-12 border-top pt-1">
                                              Earned: ৳{p.achievedAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </div>
                                          </div>
                                        </Col>
                                      ))}
                                    </Row>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">No team members found for targets.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal for Setting / Editing Target */}
        <Modal isOpen={modalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            {editingTargetId ? "Edit User Monthly Target" : "Set User Monthly Target"}
          </ModalHeader>
          <form onSubmit={handleSaveTarget}>
            <ModalBody>
              <FormGroup>
                <Label for="user">Team Member</Label>
                <Input
                  type="select"
                  id="user"
                  value={selectedUserForTarget}
                  onChange={(e) => setSelectedUserForTarget(e.target.value)}
                  required
                  disabled={!!editingTargetId}
                >
                  <option value="">Select Team Member</option>
                  {allTeamUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="month">Target Month</Label>
                <Input
                  type="month"
                  id="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="target_amount">Target Amount (BDT)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  id="target_amount"
                  placeholder="e.g. 100000"
                  value={targetAmountInput}
                  onChange={(e) => setTargetAmountInput(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="commission_rate">Commission Rate (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  id="commission_rate"
                  placeholder="e.g. 15"
                  value={commissionRateInput}
                  onChange={(e) => setCommissionRateInput(e.target.value)}
                  required
                />
                <small className="text-muted d-block mt-1">Set percentage rate for bonus/commission above target (Default: 15%)</small>
              </FormGroup>

              <FormGroup>
                <Label for="notes">Notes (Optional)</Label>
                <Input
                  type="textarea"
                  id="notes"
                  rows="3"
                  placeholder="Enter target notes or expectations..."
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal}>Cancel</Button>
              <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                {isCreating || isUpdating ? "Saving..." : "Save Target"}
              </Button>
            </ModalFooter>
          </form>
        </Modal>

      </div>
    </div>
  );
};

export default TargetManagement;
